import { schedule as scheduleService } from "services/api/company/schedule.service";
import { salary as salaryService } from "services/api/company/salary.service";
import { organizationalStructure as organizationalStructureService } from "services/api/company/organizationalStructure.service";
import { err, ok } from "utils/result";
import type { Result } from "utils/result";
import type { ShiftDetails } from "../components/common/modals/shiftDetailsModal.component";
import type { ScheduleStatus } from "model/Schedule";
import type { ResolutionDetails } from "services/api/company/schedule.service";
import type {
  CompanyWorker,
  LoadCompanyWorkersError,
  LoadOrganizationalStructureError,
  LoadShiftDetailsError,
  OrganizationalStructureListItem,
  RefreshShiftStatusError,
} from "./schedule.utils.types";

/**
 * Loads company workers and returns a Result with parsed data.
 */
export const loadCompanyWorkers = async (
  companyId: number,
): Promise<Result<CompanyWorker[], LoadCompanyWorkersError>> => {
  try {
    const response = await salaryService.getCompanyWorkers(companyId, false);
    return ok(response.data);
  } catch {
    return err("request-failed");
  }
};

/**
 * Loads organizational structure list and returns a Result with parsed data.
 */
export const loadOrganizationalStructureList = async (
  companyId: number,
): Promise<
  Result<OrganizationalStructureListItem[], LoadOrganizationalStructureError>
> => {
  try {
    const response =
      await organizationalStructureService.getOrganizationalStructureList(
        companyId,
      );
    return ok(response.data);
  } catch {
    return err("request-failed");
  }
};

/**
 * Loads shift details for a specific gig shift.
 */
export const loadShiftDetails = async (
  companyId: number,
  gigId: number,
  gigShiftId: number,
): Promise<Result<ShiftDetails | null, LoadShiftDetailsError>> => {
  try {
    const response = await scheduleService.getShiftDetails(
      companyId,
      gigId,
      gigShiftId,
    );
    return ok(response.data ?? null);
  } catch {
    return err("request-failed");
  }
};

/**
 * Loads and parses shift status for the current shift.
 */
export const loadShiftStatus = async (
  companyId: number,
  gigShiftId: number,
  workerId: number,
): Promise<
  Result<{ status: ScheduleStatus; details: ResolutionDetails[] }, RefreshShiftStatusError>
> => {
  try {
    const response = await scheduleService.getShiftStatus(
      companyId,
      gigShiftId,
      workerId,
    );
    return ok({ status: response.data.status, details: response.data.details });
  } catch {
    return err("request-failed");
  }
};
